import React from 'react';
import { Button, Stack, Typography } from '@mui/material';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ScoreboardOutlinedIcon from '@mui/icons-material/ScoreboardOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PopupMessageWrapper } from './PopupMessageWrapper';
import type { EventPushMessage } from '@moment-golf/types';
import { EVENT_PUSH_ICON_LABELS } from '@moment-golf/types';

const COPPER = 'rgb(184, 115, 51)';

function resolveIcon(icon: EventPushMessage['icon'], size: number = 56) {
    const sx = { fontSize: size, color: COPPER };
    switch (icon) {
        case 'stopwatch': return <TimerOutlinedIcon sx={sx} />;
        case 'lightning': return <BoltOutlinedIcon sx={sx} />;
        case 'score':     return <ScoreboardOutlinedIcon sx={sx} />;
        case 'sunset':    return <WbSunnyOutlinedIcon sx={sx} />;
        case 'info':
        default:          return <InfoOutlinedIcon sx={sx} />;
    }
}

interface PushMessageModalProps {
    pendingMessages: EventPushMessage[];
    reviewMessage: EventPushMessage | null;
    acknowledge: (pushedAt: string) => void;
    closeReview: () => void;
}

export const PushMessageModal = ({
    pendingMessages,
    reviewMessage,
    acknowledge,
    closeReview,
}: PushMessageModalProps) => {
    const blockingMessage = pendingMessages[0] ?? null;
    const isBlocking = blockingMessage !== null;
    const activeMessage = isBlocking ? blockingMessage : reviewMessage;
    const isOpen = isBlocking || reviewMessage !== null;

    if (!activeMessage || !isOpen) return null;

    const handleAcknowledge = () => {
        acknowledge(activeMessage.pushedAt);
        if (!isBlocking) {
            closeReview();
        }
    };

    const iconLabel = EVENT_PUSH_ICON_LABELS[activeMessage.icon] ?? 'Event';

    return (
        <PopupMessageWrapper open={isOpen}>
            <Stack direction="column" spacing={2.5} alignItems="center" sx={{ minWidth: 260, maxWidth: 400 }}>
                {resolveIcon(activeMessage.icon)}

                <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: 'sepia.dark', textAlign: 'center' }}
                >
                    {iconLabel} Update
                </Typography>

                <Typography
                    variant="body1"
                    sx={{ color: 'sepia.main', textAlign: 'center', whiteSpace: 'pre-wrap' }}
                >
                    {activeMessage.message}
                </Typography>

                <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ pt: 1, width: '100%' }}>
                    {!isBlocking && (
                        <Button
                            variant="outlined"
                            onClick={closeReview}
                            sx={{ borderColor: 'sepia.extraLight', color: 'sepia.main' }}
                        >
                            Close
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleAcknowledge}
                        sx={{
                            backgroundColor: COPPER,
                            color: '#fff',
                            fontWeight: 600,
                            '&:hover': { backgroundColor: 'rgba(184, 115, 51, 0.85)' },
                        }}
                    >
                        Got it
                    </Button>
                </Stack>
            </Stack>
        </PopupMessageWrapper>
    );
};

/** Small icon for use in the app bar and info drawer header. */
export function PushMessageIcon({ icon }: { icon: EventPushMessage['icon'] }) {
    return resolveIcon(icon, 24);
}
